import {Suspense, useState} from 'react';
import {WhiteTableGrid} from '../../tables/white-table-grid';
import {clsx} from 'clsx';
import {EvLoadingSpinner} from '../../components/loading/ev-loading-spinner';
import {useProductionStepMaterialListQuery} from './use-production-step-material-list-query';
import {
  ProductionStepProp,
  ProductionStepMaterialCondition,
  ProductionStepMaterialConditionForm,
  ProductionStepMutation,
  ProductionStepMaterialListState,
} from './production-step.models';
import {ProductionStepMaterialForm} from './production-step-edit-material-form';
import {useProductionStepMaterialCreateMutation} from './use-production-step-material-create-mutation';
import {useProductionStepMaterialUpdateMutation} from './use-production-step-material-update-mutation';
import {EvButton} from '../../form/EvButton';
import {EvLoadingSwitch} from '../../components/loading/ev-loading-switch';
import {EvReactModal} from '../../modal/EvReactModal';
import {useProductionStepMaterialDeleteMutation} from './use-production-step-material-delete-mutation';
import {ErrorBoundary} from '../../errorhandling/ErrorBoundary';
import {NumberHelper} from '../../../helper/number-helper';

export function ProductionStepMaterialList(props: ProductionStepProp) {
  const [state, setState] = useState<ProductionStepMaterialListState>({
    modal: 'closed',
    selected: null,
    createOpen: false,
    errors: {},
  });

  const createMutation = useProductionStepMaterialCreateMutation(props);
  const updateMutation = useProductionStepMaterialUpdateMutation({
    ...props,
    variationId: state.selected?.material_variation?.id,
  });

  const deleteMutation = useProductionStepMaterialDeleteMutation({
    ...props,
    variationId: state.selected?.material_variation?.id,
  });

  const toggleModal: (modal: 'closed' | 'open') => void = (modal) => {
    setState({...state, modal: modal, errors: {}, selected: null, createOpen: false});
  };

  const createOpen: () => void = () => {
    setState({...state, errors: {}, selected: null, createOpen: true});
  };

  const select: (element: ProductionStepMaterialCondition | null) => void = (element) => {
    setState({...state, errors: {}, selected: element, createOpen: false});
  };

  const parseAndMutate: (
    form: ProductionStepMaterialConditionForm,
    mutation: ProductionStepMutation,
  ) => void = (form, mutation) => {
    let parsed: any | null = null;
    try {
      parsed = JSON.parse(form.rules);
    } catch (e) {
      setState({...state, errors: {'rules': [`Kein korrekter JSON Logik syntax.\n\n${e.message}`]}});
    }

    if (parsed === null) {
      return;
    }

    mutation.mutate(
      {
        variation_id: form.variation_id,
        group: NumberHelper.saveParseInteger(form.group),
        production_machine_id: NumberHelper.saveParseInteger(form.production_machine_id),
        rules: parsed,
      },
      errors => {
        if (errors === null) {
          setState({...state, errors: {}, selected: null, createOpen: false});
        } else {
          setState({...state, ...errors});
        }
      });
  };

  const confirmDeleteAndMutate: () => void = () => {
    if (confirm(`Soll das Material \"${
      state.selected.material_variation.variation_id
    } ${
      state.selected.material_variation.article.name
    }\" wirklich von dem Produktionsschritt \"${
      props.productionStepNr
    } - ${
      props.productionStepText
    }\" entfernt werden?`)) {
      deleteMutation.mutate();
    }
  };

  const disableButtons = updateMutation.isPending || createMutation.isPending || deleteMutation.isPending;

  const create: (form: ProductionStepMaterialConditionForm) => void = (form) => {
    return parseAndMutate(form, createMutation);
  };

  const update: (form: ProductionStepMaterialConditionForm) => void = (form) => {
    return parseAndMutate(form, updateMutation);
  };

  const list = useProductionStepMaterialListQuery({...props, ...state});
  return <>
    <EvButton variant="default" size="xsm" onClick={() => toggleModal('open')}>Material</EvButton>
    <EvReactModal contentLabel={`Produktionsschritt ${props.productionStepNr} - ${props.productionStepText}`}
                  showModal={state.modal === 'open'}
                  modalClosed={() => toggleModal('closed')}>
      {
        state.modal === 'open' &&
        <div className="w-[80vw]">
          <WhiteTableGrid gridColsClass="grid-cols-10-10-10-60-10" colSpanClass="col-span-5">
            {{
              headerRows: [[
                <div key="label" className="p-2">Interne Nr.</div>,
                <div key="group" className="p-2">Gruppe</div>,
                <div key="machine" className="p-2">Maschine</div>,
                <div key="value" className="p-2">Bedingung</div>,
                <div key="buttons" className="p-2 text-right">
                  <EvButton variant="link" size="no-padding" onClick={() => createOpen()}>
                    <i className="fa fa-plus text-storz-green"/>
                  </EvButton>
                </div>,
              ]],
              body: ([gridColsClass, colSpanClass, ...classNames]) =>
                <EvLoadingSwitch query={list}>
                  {{
                    success:
                      [
                        ...(
                          !state.createOpen ?
                            [] :
                            [
                              <ErrorBoundary key={-1}>
                                <Suspense fallback={<EvLoadingSpinner/>}>
                                  <ProductionStepMaterialForm className={clsx(gridColsClass, colSpanClass, classNames)}
                                                              errors={state.errors}
                                                              condition={null}
                                                              formSubmit={create}>
                                    <div className="p-2">
                                      <EvButton className="w-full"
                                                variant="success"
                                                size="sm"
                                                type="submit"
                                                disabled={updateMutation.isPending || createMutation.isPending}>
                                        Speichern
                                      </EvButton>
                                    </div>
                                  </ProductionStepMaterialForm>
                                </Suspense>
                              </ErrorBoundary>,
                            ]
                        ),
                        ...list.objects.map(p =>
                          (
                            p.material_variation.id === state.selected?.material_variation?.id &&
                            p.production_machine_id === state.selected?.production_machine_id
                          ) ? (
                            <ErrorBoundary key={`${p.material_variation.id}-${p.production_machine_id ?? -1}`}>
                              <Suspense fallback={<EvLoadingSpinner/>}>
                                <ProductionStepMaterialForm className={clsx(gridColsClass, colSpanClass, classNames)}
                                                            errors={state.errors}
                                                            condition={p}
                                                            formSubmit={update}>
                                  <div className="p-2">
                                    <EvButton className="w-full"
                                              variant="success"
                                              size="sm"
                                              type="submit"
                                              disabled={disableButtons}>
                                      Speichern
                                    </EvButton>
                                  </div>
                                  <div className="p-2">
                                    <EvButton className="w-full"
                                              variant="danger"
                                              size="sm"
                                              disabled={disableButtons}
                                              onClick={() => select(null)}>
                                      Abbrechen
                                    </EvButton>
                                  </div>
                                  <div className="p-2">
                                    <EvButton className="w-full"
                                              variant="danger"
                                              size="sm"
                                              disabled={disableButtons}
                                              onClick={() => confirmDeleteAndMutate()}>
                                      Löschen
                                    </EvButton>
                                  </div>
                                </ProductionStepMaterialForm>
                              </Suspense>
                            </ErrorBoundary>
                          ) : (
                            <div key={`${p.material_variation.id}-${p.production_machine_id ?? -1}`}
                                 className={clsx(gridColsClass, colSpanClass, classNames)}>
                              <div className="p-2" title={p.material_variation.article.name}>
                                {p.material_variation.variation_id}
                              </div>
                              <div className="p-2">{p.group}</div>
                              <div className="p-2">
                                {p.production_machine_id} {p.production_machine?.name}
                              </div>
                              <div className="p-2">{JSON.stringify(p.rules)}</div>
                              <div className="p-2">
                                <EvButton variant="link" size="no-padding" onClick={() => select(p)}>
                                  <i className="fa fa-pencil text-black"/>
                                </EvButton>
                              </div>
                            </div>
                          )),
                      ],
                    error:
                      <div className={clsx(colSpanClass, classNames, 'text-center')}>
                        Fehler beim laden der Materialien
                      </div>,
                    loading:
                      <div className={clsx(gridColsClass, colSpanClass, classNames)}>
                        <EvLoadingSpinner/>
                      </div>,
                  }}
                </EvLoadingSwitch>,
            }}
          </WhiteTableGrid>
        </div>
      }
    </EvReactModal>
  </>;
}
